import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageShell, { Breadcrumb } from "@/components/feature/PageShell";

export default function AboutIntroduction() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleNav = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(href);
  };

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative pt-24 pb-20 bg-gradient-to-b from-primary-900 to-primary-800 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20elegant%20geometric%20pattern%20with%20warm%20golden%20lines%20and%20subtle%20circular%20motifs%20on%20deep%20burgundy%20background%2C%20luxurious%20academic%20texture%2C%20soft%20gradient%20lighting%2C%20institutional%20prestige%20aesthetic%2C%20no%20text%2C%20no%20people%2C%20clean%20minimal%20design&width=1600&height=600&seq=about-intro-hero-01&orientation=landscape')] bg-cover bg-center opacity-20"></div>
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8">
          <Breadcrumb items={[{ label: t("nav.about") }, { label: t("nav.about.intro") }]} />
          <div className="mt-6 max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight">
              {t("about.intro.title")}
            </h1>
            <p className="mt-4 text-lg text-background-100/80 leading-relaxed">
              {t("about.intro.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              {/* Mission */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">{t("about.intro.mission")}</h2>
                <p className="text-foreground-700 leading-relaxed">
                  {t("about.intro.mission.p1")}
                </p>
                <p className="text-foreground-700 leading-relaxed mt-4">
                  {t("about.intro.mission.p2")}
                </p>
              </div>

              {/* Core Values */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">{t("about.intro.values")}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {[
                    { icon: "ri-scales-3-line", key: "0" },
                    { icon: "ri-global-line", key: "1" },
                    { icon: "ri-shake-hands-line", key: "2" },
                    { icon: "ri-lightbulb-line", key: "3" },
                  ].map((v, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-6">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-3">
                        <i className={`${v.icon} text-lg`}></i>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-900 mb-2">{t(`about.intro.values.${v.key}.title`)}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{t(`about.intro.values.${v.key}.desc`)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Main Functions */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">{t("about.intro.functions")}</h2>
                <div className="space-y-4">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-4 p-5 bg-background-100/70 rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-600 text-background-50 text-sm font-bold">
                        {i + 1}
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-foreground-900 mb-1">{t(`about.intro.functions.${i}.title`)}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{t(`about.intro.functions.${i}.desc`)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              <div className="bg-primary-50 rounded-lg p-6 border border-primary-200">
                <h3 className="font-heading font-semibold text-primary-800 mb-3">{t("about.intro.sidebar.quickLinks")}</h3>
                <nav className="space-y-2">
                  <a href="/about/organization" onClick={handleNav("/about/organization")} className="block text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer py-1.5 border-b border-primary-200/50">{t("nav.about.org")}</a>
                  <a href="/about/inquiry" onClick={handleNav("/about/inquiry")} className="block text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer py-1.5 border-b border-primary-200/50">{t("nav.about.inquiry")}</a>
                  <a href="/research" onClick={handleNav("/research")} className="block text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer py-1.5">{t("nav.research")}</a>
                </nav>
              </div>

              <div className="bg-accent-50 rounded-lg p-6 border border-accent-200">
                <h3 className="font-heading font-semibold text-accent-800 mb-3">{t("about.intro.sidebar.contact")}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {t("about.intro.sidebar.contact.desc")}
                </p>
                <a href="/contact" onClick={handleNav("/contact")} className="inline-flex items-center gap-2 bg-accent-500 text-background-50 px-4 py-2 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap">
                  {t("about.intro.sidebar.contact.btn")} <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PageShell>
  );
}